import { LanguageEntry, SnapshotKpis } from "@cryptors/api-client";
import { Stack } from "@mui/material";

import { KpiCard } from "./KpiCard";

const fmt = (n: number) => new Intl.NumberFormat("fr-FR").format(n);

// Top 4 languages as "FR 45% · EN 30%", or "—" when empty/unavailable.
const formatLanguageList = (entries: LanguageEntry[] | null | undefined) =>
  entries && entries.length > 0
    ? entries
        .slice(0, 4)
        .map((l) => `${l.code.toUpperCase()} ${l.pct}%`)
        .join(" · ")
    : "—";

interface AdminKpisSectionProps {
  data: SnapshotKpis;
}

const AdminKpisSection = ({ data }: AdminKpisSectionProps) => {
  const knownPlayMode = data
    ? data.playMode.solo.count +
      data.playMode.family.count +
      data.playMode.friends.count
    : 0;
  const topPlayMode =
    data && knownPlayMode > 0
      ? [
          { label: "Solo", count: data.playMode.solo.count },
          { label: "Famille", count: data.playMode.family.count },
          { label: "Amis", count: data.playMode.friends.count },
        ].reduce((a, b) => (b.count > a.count ? b : a))
      : null;
  const topPlayModePct =
    topPlayMode !== null
      ? Math.round((topPlayMode.count / knownPlayMode) * 1000) / 10
      : null;

  return (
    <Stack direction="row" gap={2} flexWrap="wrap">
      <KpiCard
        title="Nombre de joueurs"
        value={fmt(data.players.total)}
        subtitle={`${fmt(data.registeredPlayers.total)} inscrits · ${data.registeredPlayers.pct}%`}
      />
      <KpiCard
        title="Nombre de collections"
        value={fmt(data.collections.total)}
        subtitle="avec au moins 1 cryptor publié"
      />
      <KpiCard
        title="Nombre d'œuvres publiées"
        value={fmt(data.cryptors.total)}
      />
      <KpiCard
        title="Scans de QR codes"
        value={data.scans.total !== null ? fmt(data.scans.total) : "—"}
      />
      <KpiCard title="Captures d'œuvres" value={fmt(data.captures.total)} />
      <KpiCard
        title="Nombre de pub vues"
        value={fmt(data.adViews.total)}
        subtitle="impressions publicitaires (global)"
      />
      <KpiCard
        title="Taux d'ouverture (30j)"
        value={
          data.activePlayersRatio !== null ? `${data.activePlayersRatio}%` : "—"
        }
        subtitle="part des inscrits ayant lancé l'app"
      />
      <KpiCard
        title="Nombre de joueurs ayant ouvert l'application (30j)"
        value={
          data.activePlayersCount !== null ? fmt(data.activePlayersCount) : "—"
        }
        subtitle="joueurs ayant lancé l'app"
      />
      <KpiCard
        title="Désinstallation (30j)"
        value={
          data.uninstallRatePct !== null ? `${data.uninstallRatePct}%` : "—"
        }
        subtitle="app_remove / inscrits · Android (GA4)"
      />
      <KpiCard
        title="Mode de visite dominant"
        value={
          topPlayMode !== null && topPlayModePct !== null
            ? `${topPlayModePct}% ${topPlayMode.label}`
            : "—"
        }
        subtitle={`sur ${fmt(knownPlayMode)} modes renseignés`}
      />
      <KpiCard
        title="Langues (30j)"
        value={formatLanguageList(data.languages)}
        subtitle="locale device via GA4"
      />
      <KpiCard
        title="Langues choisies dans l'app"
        value={formatLanguageList(data.appLanguages)}
        subtitle="langue sélectionnée dans l'app"
      />
      <KpiCard
        title="Âge moyen des joueurs"
        value={data.averageAge !== null ? `${data.averageAge} ans` : "—"}
      />
      {data.stores.apple.rating !== null && (
        <KpiCard
          title="Note App Store (iOS)"
          value={`${data.stores.apple.rating} / 5`}
          subtitle={
            data.stores.apple.ratingCount !== null
              ? `${fmt(data.stores.apple.ratingCount)} avis`
              : undefined
          }
        />
      )}
      {data.stores.google.rating !== null && (
        <KpiCard
          title="Note Play Store (Android)"
          value={`${data.stores.google.rating} / 5`}
          subtitle={
            data.stores.google.ratingCount !== null
              ? `${fmt(data.stores.google.ratingCount)} avis`
              : undefined
          }
        />
      )}
    </Stack>
  );
};

export default AdminKpisSection;
